import { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import Header from '../components/layout/Header'
import Sidebar from '../components/layout/Sidebar'
import ChatInterface, { Message } from '../components/chat/ChatInterface'
import DocumentPanel, { DocTab, ViewerHandle } from '../components/chat/DocumentPanel'
import { CitationSource, StreamCompleteResult } from '@/api/chatService'
import { getConversation } from '@/api/conversationService'
import {
  useConversations,
  useDeleteConversation,
  useRenameConversation,
  convertBackendMessages,
  CONVERSATIONS_KEY,
} from '@/hooks/useConversations'
import { useAuth } from '@/contexts/AuthContext'
import { getDocumentByLegalRef } from '@/api/filterService'
import DocumentUnavailableModal from '@/components/ui/DocumentUnavailableModal'

export default function ChatPage() {
  const { t } = useTranslation()
  const { preloadLegalRef } = useAuth()

  // UI state
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  // Current chat state
  const [messages, setMessages] = useState<Message[]>([])
  const [currentConversationId, setCurrentConversationId] = useState<number | null>(null)
  const [isLoadingMessages, setIsLoadingMessages] = useState(false)

  // Filter state (persisted per conversation via conversation_filters table)
  const [selectedJurisdictionUuids, setSelectedJurisdictionUuids] = useState<string[]>([])
  const [selectedPubAuthorityUuids, setSelectedPubAuthorityUuids] = useState<string[]>([])
  const [selectedDocumentLegislationUuids, setSelectedDocumentLegislationUuids] = useState<string[]>([])
  const [jurisdictionError, setJurisdictionError] = useState(false)

  // Document pre-selection from parent app
  // Reads legal_ref from iframe URL params or JWT claims (via AuthContext).
  // On success: pre-selects the matching jurisdiction and document, leaves pub-auth as "all".
  // On 404: shows the DocumentUnavailableModal so the user knows the doc isn't in the system yet.
  const [unavailableLegalRef, setUnavailableLegalRef] = useState<string | null>(null)
  const [restrictedLegalRef, setRestrictedLegalRef] = useState<string | null>(null)

  useEffect(() => {
    if (!preloadLegalRef) return
    console.debug(`[preload] Document requested — legal_ref="${preloadLegalRef}"`)
    getDocumentByLegalRef(preloadLegalRef).then(result => {
      if (result.status === 'found') {
        const doc = result.doc
        console.debug(`[preload] Document found — uuid="${doc.legislation_uuid}" title="${doc.title}" jurisdictions=[${(doc.jurisdiction_uuids ?? []).join(', ')}]`)
        setSelectedJurisdictionUuids(doc.jurisdiction_uuids ?? [])
        setSelectedPubAuthorityUuids([])
        setSelectedDocumentLegislationUuids([doc.legislation_uuid])
      } else if (result.status === 'restricted') {
        console.warn(`[preload] Access restricted — legal_ref="${preloadLegalRef}"`)
        setRestrictedLegalRef(preloadLegalRef)
      } else {
        console.warn(`[preload] Document not found — legal_ref="${preloadLegalRef}"`)
        setUnavailableLegalRef(preloadLegalRef)
      }
    }).catch(() => {
      console.warn(`[preload] Lookup failed — legal_ref="${preloadLegalRef}"`)
      setUnavailableLegalRef(preloadLegalRef)
    })
  }, [preloadLegalRef])

  // Document title map for enriched feedback filter config
  const [documentTitleMap, setDocumentTitleMap] = useState<Map<string, string>>(new Map())

  // Document panel state
  const [docPanelOpen, setDocPanelOpen] = useState(false)
  const [docPanelTabs, setDocPanelTabs] = useState<DocTab[]>([])
  const [activeTabId, setActiveTabId] = useState<string | null>(null)
  const [docPanelWidth, setDocPanelWidth] = useState(() => Math.max(380, Math.round(window.innerWidth * 0.4)))
  const docPanelRef = useRef<ViewerHandle | null>(null)
  const isDraggingRef = useRef(false)
  const dragStartXRef = useRef(0)
  const dragStartWidthRef = useRef(0)

  const handleSeparatorMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    isDraggingRef.current = true
    dragStartXRef.current = e.clientX
    dragStartWidthRef.current = docPanelWidth

    const onMouseMove = (ev: MouseEvent) => {
      if (!isDraggingRef.current) return
      const delta = dragStartXRef.current - ev.clientX
      const next = Math.min(Math.max(dragStartWidthRef.current + delta, 320), window.innerWidth * 0.65)
      setDocPanelWidth(next)
    }
    const onMouseUp = () => {
      isDraggingRef.current = false
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
  }, [docPanelWidth])

  const upsertTab = useCallback((source: CitationSource, citedText: string, positionFraction: number | null = null) => {
    if (!source.viewable || !source.legislation_uuid) return
    const id = source.legislation_uuid
    setDocPanelTabs((prev) => {
      const exists = prev.find((t) => t.id === id)
      if (exists) {
        // Empty citedText = open doc without jumping — preserve existing citation
        if (!citedText) return prev
        // Same citation re-clicked — scroll imperatively without a state change
        if (exists.citedText === citedText) {
          requestAnimationFrame(() => docPanelRef.current?.scrollToCitation())
          return prev
        }
        return prev.map((t) => t.id === id ? { ...t, citedText, positionFraction } : t)
      }
      return [...prev, {
        id,
        title: source.title,
        legislationUuid: id,
        citedText,
        positionFraction,
        isPdf: source.is_pdf,
      }]
    })
    setActiveTabId(id)
    setDocPanelOpen(true)
    setSidebarCollapsed(true)
  }, [])

  const handleCitationClick = useCallback((source: CitationSource, citedText: string, positionFraction: number | null = null) => {
    if (!source.viewable || !source.legislation_uuid) return
    upsertTab(source, citedText, positionFraction)
  }, [upsertTab])

  // Server state
  const queryClient = useQueryClient()
  const { data: conversations = [] } = useConversations()
  const deleteMutation = useDeleteConversation()
  const renameMutation = useRenameConversation()

  const closeDocPanel = useCallback(() => {
    setDocPanelOpen(false)
    setDocPanelTabs([])
    setActiveTabId(null)
  }, [])

  const handleNewChat = () => {
    setMessages([])
    setCurrentConversationId(null)
    setSelectedJurisdictionUuids([])
    setSelectedPubAuthorityUuids([])
    setSelectedDocumentLegislationUuids([])
    setJurisdictionError(false)
    closeDocPanel()
  }

  const handleLoadConversation = async (conversationId: number) => {
    if (conversationId === currentConversationId) return
    closeDocPanel()

    setIsLoadingMessages(true)
    setJurisdictionError(false)
    try {
      const detail = await getConversation(conversationId)
      setMessages(convertBackendMessages(detail.messages))
      setCurrentConversationId(conversationId)
      // Restore filters from stored config (or reset for legacy conversations)
      if (detail.filter_config) {
        setSelectedJurisdictionUuids(detail.filter_config.jurisdiction_uuids || [])
        setSelectedPubAuthorityUuids(detail.filter_config.pub_authority_uuids || [])
        setSelectedDocumentLegislationUuids(detail.filter_config.document_legislation_uuids || [])
      } else {
        setSelectedJurisdictionUuids([])
        setSelectedPubAuthorityUuids([])
        setSelectedDocumentLegislationUuids([])
      }
    } catch (error) {
      console.error('Failed to load conversation:', error)
    } finally {
      setIsLoadingMessages(false)
    }
  }

  const handleStreamComplete = (result: StreamCompleteResult) => {
    setCurrentConversationId(result.conversationId)
    // Refresh sidebar to show the new/updated conversation
    queryClient.invalidateQueries({ queryKey: CONVERSATIONS_KEY })
  }

  const handleDeleteConversation = (conversationId: number) => {
    deleteMutation.mutate(conversationId)
    if (conversationId === currentConversationId) {
      setMessages([])
      setCurrentConversationId(null)
    }
  }

  const handleRenameConversation = (conversationId: number, newTitle: string) => {
    renameMutation.mutate({ conversationId, title: newTitle })
  }

  return (
    <div className="flex h-screen bg-white overflow-hidden">
      {unavailableLegalRef && (
        <DocumentUnavailableModal
          legalRef={unavailableLegalRef}
          onDismiss={() => setUnavailableLegalRef(null)}
        />
      )}
      {restrictedLegalRef && (
        <DocumentUnavailableModal
          legalRef={restrictedLegalRef}
          restricted
          onDismiss={() => setRestrictedLegalRef(null)}
        />
      )}
      {/* Left Sidebar - Filters + Chat History */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onNewChat={handleNewChat}
        conversations={conversations}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        onLoadConversation={handleLoadConversation}
        currentConversationId={currentConversationId}
        onRenameConversation={handleRenameConversation}
        onDeleteConversation={handleDeleteConversation}
        selectedJurisdictionUuids={selectedJurisdictionUuids}
        setSelectedJurisdictionUuids={setSelectedJurisdictionUuids}
        selectedPubAuthorityUuids={selectedPubAuthorityUuids}
        setSelectedPubAuthorityUuids={setSelectedPubAuthorityUuids}
        selectedDocumentLegislationUuids={selectedDocumentLegislationUuids}
        setSelectedDocumentLegislationUuids={setSelectedDocumentLegislationUuids}
        jurisdictionError={jurisdictionError}
        onJurisdictionErrorDismiss={() => setJurisdictionError(false)}
        onDocumentMapUpdate={setDocumentTitleMap}
        showNotice={messages.length > 0}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header — only shown in active conversations */}
        {messages.length > 0 && (
          <Header
            onMenuClick={() => setSidebarOpen(!sidebarOpen)}
            title={conversations.find(c => c.conversation_id === currentConversationId)?.title || t('chat.newChat')}
          />
        )}

        {/* Chat Interface */}
        <ChatInterface
          messages={messages}
          setMessages={setMessages}
          selectedJurisdictionUuids={selectedJurisdictionUuids}
          selectedPubAuthorityUuids={selectedPubAuthorityUuids}
          selectedDocumentLegislationUuids={selectedDocumentLegislationUuids}
          onJurisdictionError={() => { setJurisdictionError(true); setSidebarCollapsed(false) }}
          currentConversationId={currentConversationId}
          onStreamComplete={handleStreamComplete}
          isLoadingMessages={isLoadingMessages}
          documentTitleMap={documentTitleMap}
          onCitationClick={handleCitationClick}
        />
      </div>

      {/* Draggable separator + Document Panel */}
      {docPanelOpen && docPanelTabs.length > 0 && (
        <>
          <div className="h-full flex-shrink-0" style={{ width: docPanelWidth }}>
            <DocumentPanel
              ref={docPanelRef}
              tabs={docPanelTabs}
              activeTabId={activeTabId}
              width={docPanelWidth}
              onResizeStart={handleSeparatorMouseDown}
              onTabSelect={setActiveTabId}
              onTabClose={(id) => {
                const remaining = docPanelTabs.filter((t) => t.id !== id)
                setDocPanelTabs(remaining)
                if (remaining.length === 0) {
                  setDocPanelOpen(false)
                  setActiveTabId(null)
                } else if (activeTabId === id) {
                  setActiveTabId(remaining[remaining.length - 1].id)
                }
              }}
              onCloseAll={() => {
                setDocPanelOpen(false)
                setDocPanelTabs([])
                setActiveTabId(null)
              }}
            />
          </div>
        </>
      )}
    </div>
  )
}
